import { useState, useEffect, useCallback } from 'react';
import { getAllWidgets, getAllDataItems, getAllHistory, getAllFolders, getFolderLinks, getAllWidgetPages } from '@/lib/db';
import type { WidgetItem, DataItem, HistoryItem, FolderItem, FolderDataLink, WidgetPage } from '@/types';

// Simple event bus for cross-component refresh
const listeners = new Set<() => void>();

export function emitDataChanged() {
  listeners.forEach((fn) => fn());
}

export function useAppData() {
  const [widgets, setWidgets] = useState<WidgetItem[]>([]);
  const [dataItems, setDataItems] = useState<DataItem[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [folderLinks, setFolderLinks] = useState<FolderDataLink[]>([]);
  const [widgetPages, setWidgetPages] = useState<WidgetPage[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [w, d, h, f, p] = await Promise.all([
        getAllWidgets(),
        getAllDataItems(),
        getAllHistory(),
        getAllFolders(),
        getAllWidgetPages(),
      ]);
      setWidgets(w);
      setDataItems(d);
      setHistory(h);
      setFolders(f);
      setWidgetPages(p);

      // Fetch all folder links
      const allLinks = await Promise.all(f.map((folder) => getFolderLinks(folder.id)));
      setFolderLinks(allLinks.flat());
    } catch (e) {
      console.error('Failed to load app data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    listeners.add(refresh);
    return () => { listeners.delete(refresh); };
  }, [refresh]);

  // Helper: get DataItem by id
  const getDataById = useCallback(
    (id: string) => dataItems.find((d) => d.id === id),
    [dataItems]
  );

  // Helper: get links for a folder
  const getLinksByFolder = useCallback(
    (folderId: string) => folderLinks.filter((l) => l.folderId === folderId),
    [folderLinks]
  );

  const getWidgetPageById = useCallback(
    (pageId: string) => widgetPages.find((page) => page.id === pageId),
    [widgetPages]
  );

  const getWidgetsByPage = useCallback(
    (pageId: string) => widgets.filter((widget) => widget.pageId === pageId),
    [widgets]
  );

  return {
    widgets,
    widgetPages,
    dataItems,
    history,
    folders,
    folderLinks,
    loading,
    refresh,
    getDataById,
    getLinksByFolder,
    getWidgetPageById,
    getWidgetsByPage,
  };
}
